import type { PayloadAction, Dispatch } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { AxiosResponse } from "axios";
import apiUrls from "../../../api";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";
import type { IMedia, ITagConfig, ITtsGeneration } from "../../../model/unityObject";
import type { SourceMedia } from "src/model/api";
import type { Field } from "../../../features/graph/util/graphFieldsExtractor";
import { getTagsOrganizationMedia } from "./filterUtil";
import type { OrganizationTag } from "./filterUtil";
import type { cUPP } from "./uploadUtil";
import { uuidv4 } from "src/common/util/uuidv4";
import { resolveDocumentId } from "src/common/util/strapiDocumentId";
import { getUserPreferences } from "src/common/util/getUserPreferences";
import { t } from "i18next";
import { setAlert } from "src/features/alert/state/alertsSlice";

export const HARDCODED_ELEVENLABS_VOICE_ID = {
  voiceId: "pFZP5JQG7iQjIQuC4Bku",
  ttsProvider: "elevenLabs",
};

type FetchFilteredMediaArgs = Partial<IMediaSearch> &
  IAppLoaderAction & {
    start?: number;
    selectedId?: number;
    skipStoreUpdate?: boolean;
  };

export interface IMediaDraft {
  name: string;
  s3Url: string;
  mimeType: string;
  media360tag: string;
  fileSizeInByte: number;
  fileName: string;
  MD5: string;
  upload_status: string;
  tags: [] | ITagUpload[];
}

export interface IBlockadeLabsModel {
  id: number;
  name: string;
  "max-char": number;
  image: string;
}

export interface IBlockadeLabsPreview {
  thumbnailUrl: string;
  imageUrl: string;
  faultyImageId?: string;
  blockadeLabImageId?: number;
  errorMessage?: string;
  id?: number;
}

export interface IBlockadeLabsStore {
  models: IBlockadeLabsModel[];
  preview?: IBlockadeLabsPreview | null;
}

export interface IElevenlabsVoice {
  id: string;
  name: string;
  language: string;
  gender: string;
  tts_provider: string;
  description: string;
  description_fr?: string;
  audio_preview_url?: string;
  is_community_voice?: boolean;
}

export interface ITtsPreview {
  url: string;
  ttsGeneration: ITtsGeneration | null;
}

interface IMultipartUploadParams {
  file: File;
  orgId: number;
  cb?: (media: IMedia) => void;
  media_360_tag: string;
  name: string;
  MD5: string;
  metadata?: Record<string, unknown>;
}

interface IPutChunkParams {
  uploadId: string;
  name: string;
  S3filename: string;
  media360tag: string;
  MD5: string;
  partNumber: number;
  file: File;
  chunkBegining: number;
  chunkEnd: number;
  unsuccessfulTry: number;
  cb?: (media: IMedia) => void;
  dispatch?: Dispatch;
  getStore?: () => RootState;
  metadata?: Record<string, unknown>;
}

interface ITagUpload {
  mediaId: number;
  orgaId: number;
  tags: ITagConfig[];
}

export interface IUploadAvatarParams {
  name: string;
  avatarUrl: string;
  voices: IUploadAvatarVoiceParams[];
}

export interface IUploadAvatarVoiceParams {
  voiceId: string;
  lang: string;
  gender: string;
  prettyVoiceName: string;
  tts_provider: string;
}
export interface IMediaSearchIndex {
  id: number;
  name: string;
  type: string;
  media_360_tag: string;
  created_at: string;
  organizationId: number;
}

export interface IMediaUpload {
  name: string;
  size_in_bytes: number;
  type: string;
  media_360_tag: string;
  MD5: string;
}

interface UploadStateConfigTags {
  newTags: ITagConfig[];
  oldTags: ITagConfig[];
}

interface ISUFPayload {
  file: File;
  metadata: cUPP;
}

export interface IMediaSearch {
  limit?: number;
  page: number;
  query?: string;
  selectedId?: string | number;
  sort: string;
  media360tag: string;
  mediaType: string;
  owner: string;
  projectGroupId?: number;
  projectId?: number;
}

export interface IDeleteTagsInAllMedias {
  dataTags: ITagConfig[];
  idSourceMedia: number;
  allMedia: IMedia[];
}

export type UploadState =
  | "in_progress"
  | "completed"
  | "pause"
  | "canceled"
  | "error"
  | "transcoding"
  | "transcribing";
export interface IUploadItem {
  uploadId: string;
  name?: string;
  s3name?: string;
  percent?: number;
  errorMessage?: string | null;
  isTranscoding?: boolean;
  size?: number;
  uploadState?: UploadState;
  uploadData?: IPutChunkParams | null;
}

export interface IUploadState {
  currentUploadsStateList: Array<IUploadItem>;
}
export interface IUpdatedWithPlaceholder {
  id: number;
  modelName: string;
  keyName: string;
  mediaType: string;
  newMediaId: number;
}

export interface IDeleteMediaResponse {
  deletedMediaId: number;
  updatedWithPlaceholders: IUpdatedWithPlaceholder[];
}

export interface IBlockadeLabsPreviewPayload {
  modelId: number;
  prompt: string;
  imagePrompt?: string;
  initStrength?: number;
  sourceMode?: "2d";
}

export type MediaByIdStatus = "loading" | "loaded" | "missing";

export interface IMediaState {
  filtered: Array<IMedia>;
  byId: Record<number, IMedia>;
  byIdStatus: Record<number, MediaByIdStatus>;
  config: {
    tags: Array<ITagConfig>;
  };
  shouldResetFilters: boolean;
  activeArrowFilterList: string;
  currentEditedMediaField: Field;
  currentMediaSidebar: number;
  currentMediaGallery: number;
  originalMediaGallery: number;
  isUploading: boolean;
  uploadedFile?: File;
  uploadedFileMetadata?: cUPP;
  uploadedOldFile?: File;
  uploadedOldFileMetadata?: cUPP;
  currentUploadsStateList: Array<IUploadItem>;
  openedSlider: string;
  filteredMediasInFlight: number;
  playingMedia: number;
  currentModeListGallery: boolean;
  activeButtonModalGalleryConfirm: boolean;
  ttsPreview?: ITtsPreview | null;
  elevenlabsVoices: IElevenlabsVoice[];
  elevenlabsVoicesStatus: "idle" | "loading" | "loaded" | "failed";
  blockadeLabs: IBlockadeLabsStore;
  search: IMediaSearch;
  searchIndex: IMediaSearchIndex[];
  numberOfSearchHits: number;
}

const initialState: IMediaState = {
  filtered: [],
  byId: {},
  byIdStatus: {},
  config: { tags: [] },
  shouldResetFilters: false,
  activeArrowFilterList: "",
  currentEditedMediaField: { fieldName: "", currentValue: 0, type: "media", mediaType: "image" },
  currentMediaSidebar: 0,
  currentMediaGallery: 0,
  originalMediaGallery: 0,
  isUploading: false,
  currentUploadsStateList: [],
  openedSlider: "",
  filteredMediasInFlight: 0,
  playingMedia: 0,
  currentModeListGallery: false,
  activeButtonModalGalleryConfirm: true,
  ttsPreview: null,
  blockadeLabs: {
    models: [],
    preview: undefined,
  },
  elevenlabsVoices: [],
  elevenlabsVoicesStatus: "idle",
  searchIndex: [],
  search: {
    limit: 20,
    page: 0,
    query: "",
    selectedId: "",
    sort: "updated_at_desc",
    media360tag: "",
    mediaType: "all",
    owner: "all",
    projectGroupId: undefined,
    projectId: undefined,
  },
  numberOfSearchHits: 20,
};

type SearchKey = keyof IMediaSearch;
type SearchValue<K extends SearchKey = SearchKey> = IMediaSearch[K];
type SearchUpdatePayload = { key: SearchKey; value: SearchValue } | Partial<IMediaSearch>;
type SingleSearchUpdate<K extends SearchKey = SearchKey> = {
  key: K;
  value: SearchValue<K>;
};

const isSingleSearchUpdate = (payload: SearchUpdatePayload): payload is SingleSearchUpdate => {
  return (
    typeof payload === "object" &&
    payload !== null &&
    Object.prototype.hasOwnProperty.call(payload, "key") &&
    Object.prototype.hasOwnProperty.call(payload, "value")
  );
};

export const fetchMediaSearchIndex = createAsyncThunk(
  "media/fetchSearchIndex",
  async ({
    projectId,
    projectGroupId,
  }: { projectId?: number; projectGroupId?: number } & IAppLoaderAction) => {
    const params: Record<string, number> = {};

    if (projectId) {
      params.projectId = projectId;
    } else if (projectGroupId) {
      params.projectGroupId = projectGroupId;
    }

    const res = await axios.get<IMediaSearchIndex[]>(apiUrls.medias.searchIndex, { params });
    return res.data;
  },
);

export const fetchMediaTags = createAsyncThunk(
  "media/fetchMediaTags",
  async ({ orgId }: { orgId: number } & IAppLoaderAction) => {
    const res = await axios.get<OrganizationTag[]>(
      `${apiUrls.medias.getTagsOrganization}?populate[organization_id]=true`,
    );
    return { tagsOrganization: res.data, orgId };
  },
);

export const getMedia = createAsyncThunk(
  "media/one",
  async ({ mediaId }: { mediaId: number } & IAppLoaderAction) => {
    return await axios
      .get<IMedia>(
        `${apiUrls.medias.all}/${mediaId}?populate[0]=organization&populate[1]=source_media_cache`,
      )
      .then((res) => res.data);
  },
);

export const fetchMediasByIds = createAsyncThunk(
  "media/fetchMediasByIds",
  async (ids: number[], { getState }) => {
    const statusMap = (getState() as RootState).media.byIdStatus;
    const uniqueIds = Array.from(new Set(ids)).filter(
      (id) => statusMap[id] === undefined || statusMap[id] === "loading",
    );

    if (uniqueIds.length === 0) {
      return { requestedIds: [], results: [] };
    }

    const res = await axios.get<{ results: IMedia[] }>(apiUrls.medias.byIds, {
      params: { ids: uniqueIds.join(",") },
    });

    return { requestedIds: uniqueIds, results: res.data.results };
  },
  {
    condition: (ids: number[], { getState }) => {
      if (ids.length === 0) return false;
      const statusMap = (getState() as RootState).media.byIdStatus;
      return ids.some((id) => statusMap[id] === undefined);
    },
  },
);

export const refreshMediaFromSse = createAsyncThunk(
  "media/refreshFromSse",
  async (mediaId: number, thunkAPI) => {
    thunkAPI.dispatch(invalidateMediaIds([mediaId]));
    await thunkAPI.dispatch(fetchMediasByIds([mediaId]));
  },
  {
    condition: (mediaId: number, { getState }) => {
      const mediaState = (getState() as RootState).media;
      return (
        mediaState.byIdStatus[mediaId] !== undefined ||
        mediaState.filtered.some((elt) => elt.id === mediaId)
      );
    },
  },
);

export interface IMediaExistsResult {
  exists: boolean;
  id: number | null;
}

export const checkMediaExists = createAsyncThunk(
  "media/checkMediaExists",
  async (params: { md5?: string; name?: string; s3Url?: string }) => {
    const { md5, name, s3Url } = params;
    if (!md5?.trim() && !name?.trim() && !s3Url?.trim()) {
      return { exists: false, id: null };
    }

    const res = await axios.get<IMediaExistsResult>(apiUrls.medias.exists, { params });
    return res.data;
  },
);

export interface IFilteredMediasResponse {
  results: IMedia[];
  totalResults: number;
}

export const fetchFilteredMedias = createAsyncThunk(
  "media/fetchFilteredMedias",
  async (
    {
      selectedId,
      projectGroupId,
      projectId,
      start = 0,
      limit,
      media360tag,
      mediaType,
      owner,
      sort,
      query,
    }: FetchFilteredMediaArgs,
    thunkAPI,
  ) => {
    const state = thunkAPI.getState() as RootState;
    const mediaSearch = state.media.search;

    const params = new URLSearchParams();

    // ✅ Merge overrides with Redux state
    const effectiveLimit = limit ?? mediaSearch.limit ?? 30;
    const effectiveSort = sort ?? mediaSearch.sort;
    const effectiveMediaType = mediaType ?? mediaSearch.mediaType;
    const effectiveMedia360tag = media360tag ?? mediaSearch.media360tag;
    const effectiveOwner = owner ?? mediaSearch.owner;
    const effectiveQuery = query ?? mediaSearch.query;
    const effectiveProjectGroupId = projectGroupId ?? mediaSearch.projectGroupId;
    const effectiveProjectId = projectId ?? mediaSearch.projectId;

    params.append("limit", effectiveLimit.toString());

    if (selectedId !== undefined) {
      params.append("selectedId", String(selectedId));
    }

    if (start) {
      params.append("start", start.toString());
    }

    if (effectiveMedia360tag) {
      params.append("media360tag", effectiveMedia360tag);
    }
    if (effectiveMediaType) {
      params.append("mediaType", effectiveMediaType);
    }
    if (effectiveOwner) {
      params.append("owner", effectiveOwner);
    }
    if (effectiveSort) {
      params.append("sort", effectiveSort);
    }
    if (effectiveQuery) {
      params.append("query", effectiveQuery);
    }
    if (effectiveProjectGroupId) {
      params.append("projectGroupId", String(effectiveProjectGroupId));
    }
    if (effectiveProjectId) {
      params.append("projectId", String(effectiveProjectId));
    }

    const url = `${apiUrls.medias.all}/filtered${params.toString() ? "?" + params.toString() : ""}`;

    const response = await axios.get<IFilteredMediasResponse>(url);

    return { data: response.data };
  },
);

export const deleteMedia = createAsyncThunk(
  "media/delete",
  async ({ mediaId }: { mediaId: IMedia["id"] } & IAppLoaderAction) => {
    const documentId = await resolveDocumentId(apiUrls.medias.all, mediaId);
    return await axios
      .delete(`${apiUrls.medias.all}/${documentId}`)
      .then((res) => res.data as IDeleteMediaResponse);
  },
);

export const deleteMultipleMediaFromIds = createAsyncThunk(
  "media/deleteMultipleMediaFromIds",
  async ({ mediaIds }: { mediaIds: Array<string | number> } & IAppLoaderAction) => {
    return await axios
      .delete(`${apiUrls.medias.deleteMultipleMedias}?mediaIds=${mediaIds.toString()}`)
      .then((res) => res.data as IDeleteMediaResponse[]);
  },
);

export interface IMediaUsageEntity {
  contentId: number | null;
  contentContentId: number | null;
  projectName: string | null;
  apiId: string | null;
  fieldName: string | null;
}

export const checkIfMediasAreUsed = createAsyncThunk(
  "media/checkIfMediasAreUsed",
  async ({
    mediaIDs,
    isToDeleteOne = false,
  }: { mediaIDs: Array<IMedia[]>; isToDeleteOne: boolean } & IAppLoaderAction) => {
    const response = await axios.post<Array<[IMediaUsageEntity[] | null, { mediaId: number }]>>(
      apiUrls.medias.checkIfMediasAreUsed,
      { data: { mediaIDs, isToDeleteOne } },
    );
    return { data: response.data };
  },
);
export const updateMediaDetail = createAsyncThunk(
  "media/update",
  async ({
    media,
    metadata,
  }: { media: IMedia; metadata?: Record<string, unknown> } & IAppLoaderAction) => {
    const updateData: Record<string, unknown> = {
      name: media.name,
      media_360_tag: media.media_360_tag,
    };
    if (metadata !== undefined) {
      updateData.metadata = metadata;
    }
    const documentId = await resolveDocumentId(apiUrls.medias.all, media.id, media);
    await axios.put<SourceMedia>(`${apiUrls.medias.all}/${documentId}`, updateData);
    return media;
  },
);

export const fetchElevenlabsVoices = createAsyncThunk(
  "media/fetchElevenlabsVoices",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    return await axios
      .get<IElevenlabsVoice[]>(`${apiUrls.medias.elevenLabs.fetchVoices}`)
      .then((res) => {
        console.log("TESTV => ", res.data);
        return res.data;
      });
  },
  {
    // Skip a second identical dispatch fired while the first is still in
    // flight (login-fanout diet, Phase 4): useEnsureElevenlabsVoices's status
    // guard is read from each component's render closure, so it can't by
    // itself stop several consumers mounting in the same commit from all
    // dispatching before the first "pending" write lands in the store.
    condition: (_arg, { getState }) =>
      (getState() as RootState).media.elevenlabsVoicesStatus !== "loading",
  },
);

export const generateVoiceOver = createAsyncThunk<
  { audio: ArrayBuffer; ttsGeneration: ITtsGeneration | null },
  {
    voice: string;
    ttsProvider: string;
    language?: string; // was used with multilingual_v2_5_turbo model; not anymo'e
    text: string;
    tags?: ITagConfig[];
    voiceSettings?: {
      stability?: number;
      similarity_boost?: number;
      temp?: number;
      cfg_coef?: number;
      padding_bonus?: number;
    };
  } & IAppLoaderAction
>(
  "media/generateVoiceOver",
  async ({ voice, ttsProvider, language, text, tags, voiceSettings }, thunkAPI) => {
    const response = await axios.post<ArrayBuffer>(
      apiUrls.medias.generateAudio,
      {
        ...(language ? { language } : {}),
        voice,
        ttsProvider,
        text,
        tags,
        ...(voiceSettings ? { voiceSettings } : {}),
      },
      {
        timeout: 300000,
        responseType: "arraybuffer",
      },
    );

    const fallbackVoiceName: unknown = response.headers?.["x-tts-voice-fallback"];
    if (typeof fallbackVoiceName === "string" && fallbackVoiceName) {
      thunkAPI.dispatch(
        setAlert({
          type: "warning",
          msg: t("pages.gallery.generateVoicePanel.voiceFallbackNotice", {
            voice: fallbackVoiceName,
          }),
        }),
      );
    }

    const rawGeneration: unknown = response.headers?.["x-tts-generation"];
    let ttsGeneration: ITtsGeneration | null = null;
    if (typeof rawGeneration === "string" && rawGeneration) {
      try {
        ttsGeneration = JSON.parse(decodeURIComponent(rawGeneration)) as ITtsGeneration;
      } catch {
        ttsGeneration = null;
      }
    }
    if (ttsGeneration && (!ttsGeneration.voiceName || !ttsGeneration.gender)) {
      const catalogVoice = (thunkAPI.getState() as RootState).media.elevenlabsVoices.find(
        (v) => v.id === ttsGeneration?.ttsVoiceId,
      );
      if (catalogVoice) {
        ttsGeneration = {
          ...ttsGeneration,
          voiceName: ttsGeneration.voiceName ?? catalogVoice.name,
          gender: ttsGeneration.gender ?? catalogVoice.gender,
        };
      }
    }

    return { audio: response.data, ttsGeneration };
  },
);

export const uploadAvatar = createAsyncThunk(
  "media/uploadAvatar",
  async ({ name, avatarUrl, voices }: IUploadAvatarParams & IAppLoaderAction) => {
    const params = {
      name,
      avatarUrl,
      voices,
    };

    const res = await axios.post<IMedia>(apiUrls.medias.uploadAvatar, { data: params });

    return res.data;
  },
);

export const updateAvatarVoices = createAsyncThunk(
  "media/updateAvatarVoices",
  async ({
    mediaId,
    voices,
  }: {
    mediaId: number;
    voices: IUploadAvatarVoiceParams[];
  } & IAppLoaderAction) => {
    const res = await axios.put<IMedia>(`${apiUrls.medias.updateAvatarVoices}/${mediaId}`, {
      data: { voices },
    });

    return res.data;
  },
);

export const duplicateAvatar = createAsyncThunk(
  "media/duplicateAvatar",
  async ({
    avatarId,
    name,
    voices,
  }: {
    avatarId: number;
    name: string;
    voices: IUploadAvatarVoiceParams[];
  } & IAppLoaderAction) => {
    const res = await axios.post<IMedia>(apiUrls.medias.duplicateAvatar, {
      data: { avatarId, name, voices },
    });

    return res.data;
  },
);

export const uploadMultipart =
  ({ file, orgId, cb, media_360_tag, name, MD5, metadata = {} }: IMultipartUploadParams) =>
  (
    dispatch: Dispatch,
    getStore: () => RootState,
  ): Promise<AxiosResponse<{ UploadId: string; S3filename: string }> | void> | number => {
    const uploadLimitInBytes = 8e18;
    const chunkSizeInBytes = 10e6;

    if (file.size <= uploadLimitInBytes) {
      const tabTmp = file.name.split(".");
      const extension = tabTmp[tabTmp.length - 1];

      let fileType = file.type;

      if (window.navigator.userAgent.includes("Safari")) {
        if (
          fileType === "" &&
          (extension.toLowerCase() === "ogg" || extension.toLowerCase() === "oga")
        ) {
          fileType = "audio/" + extension.toLowerCase();
        }
      }

      const params = {
        filename: name === "" ? file.name : name,
        mime: fileType,
        extension: extension.toLowerCase(),
        organization: orgId,
        fileSize: file.size,
        MD5: MD5,
        Metadata: { MD5 },
      };

      return axios
        .post<{ UploadId: string; S3filename: string }>(apiUrls.medias.initializeMultipart, {
          data: params,
        })
        .then((response) => {
          if (!response) throw new Error("initializeMultipart returned no response");
          const s3name =
            response.data.S3filename.split("/")[response.data.S3filename.split("/").length - 1];
          dispatch(
            addUploadToCurrentList({
              s3name,
              uploadId: response.data.UploadId,
              name: name === "" ? file.name : name,
              percent: 0,
              errorMessage: null,
              isTranscoding: false,
              size: file.size,
              uploadState: "in_progress",
            }),
          );
          setTimeout(
            () =>
              putChunk({
                uploadId: response.data.UploadId,
                name: name === "" ? file.name : name,
                S3filename: response.data.S3filename,
                media360tag: media_360_tag,
                MD5,
                partNumber: 1,
                file,
                chunkBegining: 0,
                chunkEnd: chunkSizeInBytes,
                unsuccessfulTry: 0,
                cb,
                metadata,
              })(dispatch, getStore),
            0,
          );
          return response;
        })
        .catch((e: unknown) => {
          console.warn(e);
        });
    }
    return 1;
  };

export const askForPublicationOf3DEnvironment =
  ({ file, name }: { file: File; name?: string }) =>
  async (): Promise<AxiosResponse<{ isSended: boolean; redirected?: boolean }> | undefined> => {
    const params = {
      fileName: name === "" ? file.name : name,
    };

    let response: AxiosResponse<{ isSended: boolean; redirected?: boolean }> | undefined;
    await axios
      .post<{ isSended: boolean; redirected?: boolean }>(
        apiUrls.medias.askForPublicationOf3DEnvironment,
        { data: params },
      )
      .then((res) => {
        response = res;
      })
      .catch((e: unknown) => {
        console.warn(e);
      });

    return response;
  };

const isErrorWithDataMessage = (e: unknown): e is { data?: { error?: { message?: string } } } =>
  typeof e === "object" && e !== null && "data" in e;

const errorMessageOf = (e: unknown): string =>
  axios.isAxiosError<{ message?: string }>(e)
    ? e.response?.data?.message ?? e.message
    : e instanceof Error
    ? e.message
    : String(e);

const putChunk =
  ({
    uploadId,
    name,
    S3filename,
    media360tag,
    MD5,
    partNumber,
    file,
    chunkBegining,
    chunkEnd,
    unsuccessfulTry,
    cb,
    metadata = {},
  }: IPutChunkParams) =>
  (dispatch: Dispatch, getStore: () => RootState) => {
    const waitAfterUnsuccessfulInMs = 6000;
    const waitAfterSuccessfullInMs = 0;
    const chunkSizeInBytes = 10e6;
    let percent = 0;
    const uploadList: IUploadItem[] = getStore().media.currentUploadsStateList;
    const currUpload: IUploadItem | undefined = uploadList.find((elt) => elt.uploadId === uploadId);
    if (currUpload?.uploadState === "canceled") {
      return;
    }

    if (chunkEnd > file.size) {
      chunkEnd = file.size;
    }

    const getSignedUploadLinkData = {
      UploadId: uploadId,
      PartNumber: partNumber,
      S3filename: S3filename,
    };

    axios
      .post<string>(apiUrls.medias.getSignedPartUploadLink, { data: getSignedUploadLinkData })
      .then((response) => {
        const chunk = file.slice(chunkBegining, chunkEnd);

        const axiosUploadInstance = axios.create();
        axiosUploadInstance.defaults.headers.common["Authorization"] = undefined;

        axiosUploadInstance
          .put(response.data, chunk)
          .then(() => {
            percent = (chunkEnd / file.size) * 100;
            unsuccessfulTry = 0;
            dispatch(
              addUploadToCurrentList({
                uploadId: uploadId,
                percent: percent,
              }),
            );

            if (chunkEnd === file.size) {
              const confirmData = {
                UploadId: uploadId,
                S3filename: S3filename,
              };

              axios
                .post<string>(apiUrls.medias.completeMultipartUpload, { data: confirmData })
                .then((confirmResponse) => {
                  let fileType = file.type;
                  if (window.navigator.userAgent.includes("Safari")) {
                    const tabTmp = file.name.split(".");
                    const extension = tabTmp[tabTmp.length - 1];

                    if (
                      fileType === "" &&
                      (extension.toLowerCase() === "ogg" || extension.toLowerCase() === "oga")
                    ) {
                      fileType = "audio/" + extension.toLowerCase();
                    }
                  }

                  void addMediaToDatabase({
                    name,
                    s3Url: confirmResponse.data,
                    mimeType: fileType,
                    media360tag,
                    fileSizeInByte: file.size,
                    fileName: S3filename,
                    cb,
                    dispatch,
                    uploadId,
                    MD5,
                    metadata,
                  });
                })
                .catch((e: unknown) => {
                  console.error("error => ", e);
                  throw e;
                });
            } else {
              setTimeout(
                () =>
                  putChunk({
                    uploadId,
                    name,
                    S3filename,
                    media360tag,
                    MD5,
                    partNumber: partNumber + 1,
                    file,
                    chunkBegining: chunkBegining + chunkSizeInBytes,
                    chunkEnd: chunkEnd + chunkSizeInBytes,
                    unsuccessfulTry: 0,
                    cb,
                  })(dispatch, getStore),
                waitAfterSuccessfullInMs,
              );
            }
          })
          .catch((e: unknown) => {
            console.error("error => ", e);
            unsuccessfulTry += 1;
            setTimeout(
              () =>
                putChunk({
                  uploadId,
                  name,
                  S3filename,
                  media360tag,
                  MD5,
                  partNumber,
                  file,
                  chunkBegining,
                  chunkEnd,
                  unsuccessfulTry,
                  cb,
                  dispatch,
                })(dispatch, getStore),
              waitAfterUnsuccessfulInMs,
            );
          });
      })
      .catch((e: unknown) => {
        console.error("error => ", e);
        dispatch(
          addUploadToCurrentList({
            uploadId: uploadId,
            percent: percent,
            errorMessage: errorMessageOf(e),
            uploadState: "error",
          }),
        );
      });
  };

export const addMediaToDatabase = ({
  name,
  s3Url,
  mimeType,
  media360tag,
  fileSizeInByte,
  fileName,
  cb,
  dispatch,
  uploadId = "",
  MD5,
  tts,
  metadata = {},
}: {
  name: string;
  s3Url: string;
  mimeType: string;
  media360tag: string;
  fileSizeInByte: number;
  fileName: string;
  cb?: (media: IMedia) => void;
  dispatch: Dispatch;
  uploadId: string;
  MD5: string;
  tts?: boolean;
  metadata?: Record<string, unknown>;
}) => {
  const uploadState = mimeType.includes("pdf") ? "transcribing" : "completed";

  const formData = {
    name: name,
    s3Url: s3Url,
    mimeType: mimeType,
    media360tag: media360tag,
    fileSizeInByte: fileSizeInByte,
    fileName: fileName,
    MD5: MD5,
    upload_status: uploadState,
    metadata,
  };

  return axios
    .post<IMedia>(apiUrls.medias.addMediaToDatabase, { data: formData })
    .then((response) => {
      if (cb) {
        cb(response.data);
      }
      if (!tts) {
        dispatch(
          addUploadToCurrentList({
            uploadId: uploadId,
            percent: 100,
            uploadState,
          }),
        );
      }

      dispatch(addMedia(response.data));
      return response.data;
    })
    .catch((e: unknown) => {
      console.error("error => ", e);
      dispatch(
        addUploadToCurrentList({
          uploadId: uploadId,
          percent: 100,
          errorMessage: errorMessageOf(e),
          uploadState: "error",
        }),
      );
    });
};

/** This one is used to 'retry transcription'. */
export const transcribePdf = createAsyncThunk(
  "media/transcribePdf",
  async ({ mediaId }: { mediaId: number } & IAppLoaderAction) => {
    try {
      await axios.post(apiUrls.medias.transcribeMedia, { mediaId });
      return { mediaId };
    } catch (err: unknown) {
      console.error("transcribePdf error:", err);
      const message = axios.isAxiosError<{ error?: { message?: string } }>(err)
        ? err.response?.data?.error?.message ?? "Failed to start transcription"
        : "Failed to start transcription";
      throw new Error(message, { cause: err });
    }
  },
);

export const addBlockadeImageToDatabase = ({
  name,
  imageUrl,
  thumbnailUrl,
  fileSizeInByte,
  fileName,
  cb,
  dispatch,
  prompt,
  MD5,
}: {
  name: string;
  imageUrl: string;
  thumbnailUrl: string;
  fileSizeInByte: number;
  fileName: string;
  cb?: (media: IMedia) => void;
  dispatch: Dispatch;
  prompt: string;
  MD5: string;
}) => {
  const uploadState = "completed";

  const formData = {
    name: name,
    imageUrl: imageUrl,
    thumbnailUrl: thumbnailUrl,
    fileSizeInByte: fileSizeInByte,
    fileName: fileName,
    MD5: MD5,
    upload_status: uploadState,
    prompt,
  };

  return axios
    .post<IMedia>(apiUrls.medias.blockadeLabs.addBlockadeImageToDatabase, { data: formData })
    .then((response) => {
      if (cb) {
        cb(response.data);
      }
      dispatch(
        addUploadToCurrentList({
          uploadId: uuidv4(),
          name,
          percent: 100,
          uploadState,
        }),
      );

      dispatch(addMedia(response.data));
      return response.data;
    })
    .catch((e: unknown) => {
      console.error("error => ", e);
      dispatch(
        addUploadToCurrentList({
          uploadId: uuidv4(),
          percent: 100,
          name,
          errorMessage: errorMessageOf(e),
          uploadState: "error",
        }),
      );
    });
};

export const fetchBlockadeLabsImageModels = createAsyncThunk(
  "media/fetchBlockadeLabsModels",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    return await axios
      .get<IBlockadeLabsModel[]>(`${apiUrls.medias.blockadeLabs.models}`)
      .then((res) => res.data);
  },
);

export const generateBlockadeLabsImagePreview = createAsyncThunk(
  "media/generateBlockadeLabsImage",
  async ({
    modelId,
    prompt,
    imagePrompt = undefined,
    initStrength = undefined,
    sourceMode = undefined,
  }: IBlockadeLabsPreviewPayload & IAppLoaderAction) => {
    try {
      const response = await axios.post<IBlockadeLabsPreview>(
        apiUrls.medias.blockadeLabs.generate,
        {
          skybox_style_id: modelId,
          prompt,
          imagePrompt,
          initStrength,
          sourceMode,
        },
      );
      return response.data;
    } catch (e: unknown) {
      console.error("Error in generateBlockadeLabsImagePreview: ", e);
      const message =
        (isErrorWithDataMessage(e) && e.data?.error?.message) ||
        (axios.isAxiosError<{ error?: { message?: string } }>(e) &&
          e.response?.data?.error?.message) ||
        (e instanceof Error && e.message) ||
        "Unknown error during image generation";
      throw new Error(message, { cause: e });
    }
  },
);

export const mediaSlice = createSlice({
  name: "media",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchMediasByIds.pending, (state, { meta }) => {
      for (const id of meta.arg) {
        if (state.byIdStatus[id] === undefined) {
          state.byIdStatus[id] = "loading";
        }
      }
    });

    builder.addCase(fetchMediasByIds.fulfilled, (state, { payload }) => {
      const resultsById = new Map(payload.results.map((media) => [media.id, media]));
      for (const media of payload.results) {
        state.byId[media.id] = media;
        state.byIdStatus[media.id] = "loaded";
      }
      if (resultsById.size > 0 && state.filtered.some((cur: IMedia) => resultsById.has(cur.id))) {
        state.filtered = state.filtered.map((cur: IMedia) => resultsById.get(cur.id) ?? cur);
      }
      for (const id of payload.requestedIds) {
        if (!resultsById.has(id)) {
          state.byIdStatus[id] = "missing";
        }
      }
    });

    builder.addCase(fetchMediasByIds.rejected, (state, { meta }) => {
      for (const id of meta.arg) {
        if (state.byIdStatus[id] === "loading") {
          state.byIdStatus[id] = "missing";
        }
      }
    });

    builder.addCase(fetchFilteredMedias.pending, (state) => {
      state.filteredMediasInFlight += 1;
    });

    builder.addCase(fetchFilteredMedias.fulfilled, (state, { payload, meta }) => {
      state.filteredMediasInFlight = Math.max(0, state.filteredMediasInFlight - 1);
      if (meta.arg.skipStoreUpdate) return;
      state.filtered = payload.data.results;
      state.numberOfSearchHits = Number(payload.data.totalResults);
      state.search.selectedId = "";
    });

    builder.addCase(fetchFilteredMedias.rejected, (state) => {
      state.filteredMediasInFlight = Math.max(0, state.filteredMediasInFlight - 1);
    });

    builder.addCase(fetchMediaTags.fulfilled, (state, { payload }) => {
      state.config.tags = getTagsOrganizationMedia(payload.tagsOrganization, payload.orgId);
    });
    builder.addCase(updateMediaDetail.fulfilled, (state, { payload }) => {
      state.filtered = [...state.filtered].map((cur: IMedia) =>
        cur.id === payload.id ? payload : cur,
      );
      if (state.byIdStatus[payload.id] !== undefined) {
        state.byId[payload.id] = payload;
        state.byIdStatus[payload.id] = "loaded";
      }
    });
    builder.addCase(updateAvatarVoices.fulfilled, (state, { payload, meta }) => {
      state.filtered = [...state.filtered].map((cur: IMedia) =>
        cur.id === payload.id ? { ...cur, metadata: payload.metadata } : cur,
      );
      const mediaId = meta.arg.mediaId;
      if (state.byId[mediaId]) {
        state.byId[mediaId] = { ...state.byId[mediaId], metadata: payload.metadata };
      }
    });
    builder.addCase(getMedia.fulfilled, (state, { payload }) => {
      if (Object.prototype.hasOwnProperty.call(payload, "id")) {
        state.filtered = [...state.filtered].map((cur: IMedia) =>
          Number(cur.id) === Number(payload.id) ? payload : cur,
        );
        state.byId[payload.id] = payload;
        state.byIdStatus[payload.id] = "loaded";
      }
    });
    builder.addCase(deleteMedia.fulfilled, (state, { payload }) => {
      state.filtered = [...state.filtered].filter((cur: IMedia) => {
        return Number(cur.id) !== Number(payload.deletedMediaId);
      });
      delete state.byId[payload.deletedMediaId];
      state.byIdStatus[payload.deletedMediaId] = "missing";
    });
    builder.addCase(deleteMultipleMediaFromIds.fulfilled, (state, { payload }) => {
      const deletedIds = new Set(
        payload
          .map((elt: { deletedMediaId: number }) => Number(elt.deletedMediaId))
          .filter((id: number) => !Number.isNaN(id)),
      );
      state.filtered = [...state.filtered].filter((cur: IMedia) => !deletedIds.has(Number(cur.id)));
      deletedIds.forEach((id) => {
        delete state.byId[id];
        state.byIdStatus[id] = "missing";
      });
    });
    builder.addCase(generateVoiceOver.fulfilled, (state, { payload }) => {
      const createBlobUrl = (audioBuffer: ArrayBuffer) => {
        const blob = new Blob([audioBuffer], { type: "audio/mpeg" });
        return URL.createObjectURL(blob);
      };

      const blobUrl = createBlobUrl(payload.audio);
      state.ttsPreview = { url: blobUrl, ttsGeneration: payload.ttsGeneration };
    });
    builder.addCase(fetchElevenlabsVoices.pending, (state) => {
      state.elevenlabsVoicesStatus = "loading";
    });
    builder.addCase(fetchElevenlabsVoices.rejected, (state) => {
      state.elevenlabsVoicesStatus = "failed";
    });
    builder.addCase(fetchElevenlabsVoices.fulfilled, (state, { payload }) => {
      state.elevenlabsVoices = payload.slice().sort((a, b) => a.name.localeCompare(b.name));
      state.elevenlabsVoicesStatus = "loaded";
    });
    // updates of the store are done in deleteMedia.then(...)
    // (it was too painful to dispatch in other slices from here)
    builder.addCase(fetchBlockadeLabsImageModels.fulfilled, (state, { payload }) => {
      state.blockadeLabs.models = payload;
    });
    builder.addCase(generateBlockadeLabsImagePreview.fulfilled, (state, { payload }) => {
      state.blockadeLabs.preview = payload;
    });
    builder.addCase(generateBlockadeLabsImagePreview.rejected, (state) => {
      state.blockadeLabs.preview = null;
    });
    builder.addCase(generateVoiceOver.rejected, (state) => {
      state.ttsPreview = null;
    });
    builder.addCase(fetchMediaSearchIndex.fulfilled, (state, { payload }) => {
      state.searchIndex = payload;
    });
    builder.addCase(transcribePdf.fulfilled, (state, { payload }) => {
      state.filtered = state.filtered.map((media) =>
        media.id === payload.mediaId ? { ...media, upload_status: "transcribing" } : media,
      );

      if (state.byId[payload.mediaId]) {
        state.byId[payload.mediaId] = {
          ...state.byId[payload.mediaId],
          upload_status: "transcribing",
        };
      }

      const media = state.byId[payload.mediaId];
      const alreadyInUploadList = state.currentUploadsStateList.some(
        (upload) => upload.s3name === media?.filename,
      );

      if (!alreadyInUploadList && media) {
        state.currentUploadsStateList.push({
          uploadId: uuidv4(),
          name: media.name,
          s3name: media.filename,
          size: media.size_in_bytes,
          uploadState: "transcribing",
          percent: 100,
        });
      } else {
        state.currentUploadsStateList = state.currentUploadsStateList.map((upload) => {
          if (upload.s3name === media?.filename) {
            return {
              ...upload,
              uploadState: "transcribing",
            };
          }
          return upload;
        });
      }
    });
  },
  reducers: {
    setCurrentMediaSidebar: (state: IMediaState, action: PayloadAction<number>) => {
      state.currentMediaSidebar = action.payload;
    },
    setCurrentMediaGallery: (state: IMediaState, action: PayloadAction<number>) => {
      state.currentMediaGallery = action.payload;
    },
    setOriginalMediaGallery: (state: IMediaState, action: PayloadAction<number>) => {
      state.originalMediaGallery = action.payload;
    },
    setUploadedFile: (state: IMediaState, action: PayloadAction<ISUFPayload | undefined>) => {
      if (action.payload) {
        state.uploadedFile = action.payload.file;
        state.uploadedFileMetadata = action.payload.metadata;
      } else {
        state.uploadedFile = undefined;
        state.uploadedFileMetadata = undefined;
      }
    },
    setUploadedOldFile: (state: IMediaState, action: PayloadAction<ISUFPayload | undefined>) => {
      if (action.payload) {
        state.uploadedOldFile = action.payload.file;
        state.uploadedOldFileMetadata = action.payload.metadata;
      } else {
        state.uploadedOldFile = undefined;
        state.uploadedOldFileMetadata = undefined;
      }
    },
    setCurrentModeListGallery: (state: IMediaState, action: PayloadAction<boolean>) => {
      state.currentModeListGallery = action.payload;
    },

    setUploadNewTagsInSuggestions: (
      state: IMediaState,
      action: PayloadAction<UploadStateConfigTags>,
    ) => {
      const tagsInStore = action.payload.oldTags;
      const newTag = action.payload.newTags;
      const tagsNewArray = tagsInStore.concat(newTag);
      const uniqueIds: number[] = [];

      const uniqueTags = tagsNewArray.filter((element) => {
        const isDuplicate = uniqueIds.includes(element.id);
        if (!isDuplicate) {
          uniqueIds.push(element.id);
          return true;
        }
        return false;
      });
      state.config.tags = uniqueTags;
    },
    addMedia: (state: IMediaState, action: PayloadAction<IMedia>) => {
      state.filtered.unshift(action.payload);
      state.byId[action.payload.id] = action.payload;
      state.byIdStatus[action.payload.id] = "loaded";
      state.shouldResetFilters = true;
      state.activeArrowFilterList = "";
    },
    invalidateMediaIds: (state: IMediaState, action: PayloadAction<number[]>) => {
      action.payload.forEach((id) => {
        delete state.byIdStatus[id];
      });
    },
    setActiveArrowFilterList: (state: IMediaState, action: PayloadAction<string>) => {
      state.activeArrowFilterList = action.payload;
    },
    addUploadToCurrentList: (state: IMediaState, action: PayloadAction<IUploadItem>) => {
      if (!state.currentUploadsStateList) {
        state.currentUploadsStateList = [];
      }
      const index = state.currentUploadsStateList.findIndex((e) => {
        return e.uploadId === action.payload.uploadId;
      });
      if (index === -1) {
        state.currentUploadsStateList.push(action.payload);
      } else {
        state.currentUploadsStateList[index] = {
          ...state.currentUploadsStateList[index],
          ...action.payload,
        };
      }
    },
    setUploadCurrentList: (state: IMediaState, action: PayloadAction<IUploadItem[]>) => {
      state.currentUploadsStateList = action.payload;
    },

    setCurrentButtonModalGalleryConfirm: (state: IMediaState, action: PayloadAction<boolean>) => {
      state.activeButtonModalGalleryConfirm = action.payload;
    },
    clearGallery: (state: IMediaState) => {
      Object.assign(state, initialState);
    },
    setOpenedSlider: (state: IMediaState, action: PayloadAction<string>) => {
      state.openedSlider = action.payload;
    },
    resetTtsPreview: (state) => {
      state.ttsPreview = null;
    },
    setBlockadeLabsImagePreview: (
      state: IMediaState,
      action: PayloadAction<IBlockadeLabsPreview>,
    ) => {
      if (Number(action.payload.blockadeLabImageId) === Number(state.blockadeLabs.preview?.id)) {
        state.blockadeLabs.preview = action.payload;
      }
    },
    resetBlockadeLabsImagePreview: (state) => {
      state.blockadeLabs.preview = null;
    },

    setMediaSearch: (state: IMediaState, action: PayloadAction<SearchUpdatePayload>) => {
      if (isSingleSearchUpdate(action.payload)) {
        const { key, value } = action.payload;
        Object.assign(state.search, { [key]: value });
        if (key === "selectedId") {
          state.search.page = 0;
          state.search.sort = "updated_at_desc";
          state.search.media360tag = "";
          state.search.mediaType = "all";
          state.search.owner = "all";
        }
      } else {
        Object.assign(state.search, action.payload);
      }
    },

    resetMediaSearch: {
      reducer(state: IMediaState, action: PayloadAction<{ resetEverything?: boolean }>) {
        const defaultSearch = {
          limit: 20,
          page: 0,
          query: "",
          selectedId: "",
          sort: "updated_at_desc",
          media360tag: "",
          mediaType: "all",
          owner: "all",
        };

        if (action.payload.resetEverything) {
          state.search = { ...defaultSearch };
        } else {
          // partial reset — keep projectId & projectGroupId ~
          state.search = {
            ...state.search,
            limit: 20,
            page: 0,
            query: "",
            selectedId: "",
            sort: "updated_at_desc",
            media360tag: "",
            mediaType: "all",
            owner: "all",
          };
        }
      },
      prepare: (payload?: { resetEverything?: boolean }) => ({
        payload: payload ?? {},
      }),
    },

    setUploadHasBeenTranscribed: (state: IMediaState, action: PayloadAction<number | string>) => {
      const mediaId = Number(action.payload);
      const media = state.byId[mediaId];

      if (media) {
        state.currentUploadsStateList = state.currentUploadsStateList.map(
          (currentUpload: IUploadItem) =>
            media.filename === currentUpload.s3name
              ? { ...currentUpload, uploadState: "completed" }
              : currentUpload,
        );
        state.byId[mediaId] = { ...media, upload_status: "completed" };
      }

      state.filtered = state.filtered.map((cur: IMedia) =>
        Number(cur.id) === mediaId ? { ...cur, upload_status: "completed" } : cur,
      );
    },
  },
});

export const mediaConfigSelector = (state: RootState) => state.media.config;

export const mediaByIdMapSelector = (state: RootState) => state.media.byId;
export const mediaByIdStatusMapSelector = (state: RootState) => state.media.byIdStatus;
export const getFilteredMedias = (state: RootState) => state.media.filtered;

export const isUploading = (state: RootState) => state.media.isUploading;
export const currentMediaSelector = (state: RootState) => {
  const id = Number(state.media.currentMediaSidebar);
  return (
    state.media.filtered.find((media: IMedia) => Number(media.id) === id) || state.media.byId[id]
  );
};
export const getCurrentEditedMediaField = (state: RootState) => state.media.currentEditedMediaField;
export const getCurrentMediaGallery = (state: RootState) => {
  const id = Number(state.media.currentMediaGallery);
  return (
    state.media.filtered.find((media: IMedia) => Number(media.id) === id) || state.media.byId[id]
  );
};
export const getCurrentMediaGalleryId = (state: RootState) => state.media.currentMediaGallery;
export const getOriginalMediaGalleryId = (state: RootState) => state.media.originalMediaGallery;
export const getUploadedFile = (state: RootState) => state.media.uploadedFile;
export const getUploadedOldFile = (state: RootState) => state.media.uploadedOldFile;
export const getUploadedFileMetadata = (state: RootState) => state.media.uploadedFileMetadata;
export const getCurrentUploadsStateList = (state: RootState) => state.media.currentUploadsStateList;
export const getCurrentTagsOrganizationId = (state: RootState) => state.media.config.tags;
export const getOpenedSlider = (state: RootState) => state.media.openedSlider;
export const getIsFilteredMediasLoading = (state: RootState) =>
  state.media.filteredMediasInFlight > 0;
export const mediaReducer = mediaSlice.reducer;
export const getCurrentModeListGallery = (state: RootState) => state.media.currentModeListGallery;
export const getElevenlabsVoices = (state: RootState) => state.media.elevenlabsVoices;
export const getElevenlabsVoicesStatus = (state: RootState) => state.media.elevenlabsVoicesStatus;
export const getTtsPreview = (state: RootState) => state.media.ttsPreview;
export const getBlockadeLabsModels = (state: RootState) => state.media.blockadeLabs.models;
export const getBlockadeLabsPreview = (state: RootState) => state.media.blockadeLabs.preview;
export const getMediaSearch = (state: RootState) => state.media.search;
export const getMediaSearchIndex = (state: RootState) => state.media.searchIndex;
export const getSearchLimit = (state: RootState) => state.media.search.limit;
export const getNumberOfMediaSearchHits = (state: RootState) => state.media.numberOfSearchHits;
export const getPreferredElevenLabsVoice = (state: RootState) => {
  const prefs = getUserPreferences({ currentUser: state.profile.currentUser }) ?? {};
  const favorite = prefs.favoriteVoiceId;

  const foundVoice = state.media.elevenlabsVoices.find((voice) => voice.id === favorite);
  if (favorite && foundVoice) {
    return {
      voiceId: favorite,
      ttsProvider: foundVoice.tts_provider,
    };
  }

  const output = state.media.elevenlabsVoices[0]?.id
    ? {
        voiceId: state.media.elevenlabsVoices[0]?.id,
        ttsProvider: state.media.elevenlabsVoices[0]?.tts_provider,
      }
    : HARDCODED_ELEVENLABS_VOICE_ID;

  return output;
};

export const {
  addMedia,
  invalidateMediaIds,
  setCurrentModeListGallery,
  setCurrentMediaSidebar,
  setCurrentMediaGallery,
  setOriginalMediaGallery,
  setCurrentButtonModalGalleryConfirm,
  setUploadedFile,
  setUploadedOldFile,
  setUploadNewTagsInSuggestions,
  addUploadToCurrentList,
  setUploadCurrentList,
  clearGallery,
  setOpenedSlider,
  setMediaSearch,
  resetMediaSearch,
  setActiveArrowFilterList,
  resetTtsPreview,
  setBlockadeLabsImagePreview,
  resetBlockadeLabsImagePreview,
  setUploadHasBeenTranscribed,
} = mediaSlice.actions;
